//@ts-ignore
import { ApiErrorResponse, ApisauceInstance, create } from 'apisauce';
import Reactotron from 'reactotron-react-js';

import { urls, useReactotron } from '../config/configConstants';
import storage from '../utils/storage';
import { LOGIN } from '../utils/constants/routes';
import { notify } from '../utils/helpers';

const AUTHORIZATION_HEADER_NAME = 'Authorization';
/**
 * Network instance that wraps the request/response interaction with our backend.
 *
 * Important flow to understand:
 * When user logs into the application, the authorization header will be set via {@link setAccessToken} method.
 * Every following request will hold this header until either the access token expires or user logs out.
 * - When access token expires, the {@link refreshToken} operation will again set the header.
 *
 * This may look odd in situation where if multiple requests are executed concurrently, one may think that
 * if token expires in between, all requests will reuse the expired access token and fail,
 * however the responsibility of operations {@link refreshToken} and {@link callApi} will be
 * to synchronize the refreshing of the access token, calling the {@link setAccessToken} only once
 * and re-trying the requests afterwards.
 **/
export const network: ApisauceInstance = create({
  baseURL: urls.backend
});

export const aiNetwork: ApisauceInstance = create({
  baseURL: urls.aiService
});

export const setAccessToken = (accessToken: any) => {
  network.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
  aiNetwork.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
};

export const removeAccessToken = () => {
  network.deleteHeader(AUTHORIZATION_HEADER_NAME);
  aiNetwork.deleteHeader(AUTHORIZATION_HEADER_NAME);
};

export const changeNetworkUrl = (url: any) => {
  network.setBaseURL(url);
};

export const changeAiNetworkUrl = (url: any) => {
  aiNetwork.setBaseURL(url);
};

/**
 * helper method to prevent app crashing when backend is down (response.data is null in that case)
 */
export const getErrorMessage = (response: ApiErrorResponse<any>) => response.data && response.data.message;

export const getErrorCode = (response: ApiErrorResponse<any>) =>
  response.data && response.data.errorCodes && response.data.errorCodes[0];

export function handleErrorResponse(response: ApiErrorResponse<any>, history, isLoginRequest?: boolean) {
  const errorMessage = getErrorMessage(response);
  const errorCode = getErrorCode(response);
  if (!errorMessage) {
    notify('Something went wrong');
    return;
  }
  if (errorCode === 401 && !isLoginRequest) {
    storage.clearSession();
    removeAccessToken();
    history.push(LOGIN);
  } else {
    notify(errorMessage);
  }
}

if (useReactotron) {
  // @ts-ignore
  network.addMonitor(Reactotron.apisauce);
}
